/*
 * Copyright (c) 2023 Puratos. All rights reserved.
 */

import React, { Component } from 'react';
import { connect } from 'react-redux';
import { fetchInterestingProducts } from '../../../../actions/my-puratos/interestingProducts.action';
import CartRow from '../../molecules/list/v2/CartRow';
import {
    addItemsToCart,
    clearCart,
    deleteItem,
    fetchCart,
    fetchCartModel,
    fetchQuickAddItems,
    updateItem,
} from '../../../../actions/my-puratos/cart.action';
import CartList from '../../organisms/list/CartList';
import { getRoute, PageRoutes } from '../../../../api/utils/aemEndpointUtil';
import { setCheckoutStep } from '../../../../actions/my-puratos/checkout.action';
import { AddToCartContextProvider } from '../../hoc/AddToCartAlertContext';
import { fetchExcelImportModel } from '../../../../actions/my-puratos/excelImport.action';
import { WithRole } from '../../hoc/withRole';
import { RoleMatrix } from '../../../../utils/my-puratos/roleUtils';
import { Redirect } from 'react-router-dom';
import { CartPage } from '../../templates/CartPage';
import CheckoutCard from '../../molecules/checkout/CheckoutCard';
import CartOverview from '../../atoms/checkout/CartOverview';
import CartInfo from '../../atoms/checkout/CartInfo';
import Alerts from '../../molecules/alert/Alerts';
import { DesktopOnly, MobileOnly, Table } from '../../atoms/table/Table';
import { BundleCartRow } from '../../molecules/list/v2/BundleCartRow';
import { CartHeader } from '../../molecules/list/v2/CartHeader';
import { getCurrencyCode, isCartLoaded, isCartLoading } from '../../../../selectors/cart';
import { isLoadingById } from '../../../../selectors/loader';
import { FETCH_CART } from '../../../../actions/cart';
import { CheckoutPromo } from '../../atoms/checkout/CheckoutPromo';
import { getCartRecommendationsModel } from '../../../../actions/my-puratos/cartRecommendations.action';
import { CartRecommendations } from '../../../components/CartRecommendations';
import { ShowHide } from '../../electrons/ShowHide';
import { GTMContextProvider } from '../../../../analytics/ga4/utils/GTMContext';
import { openModal } from '../../../../actions/modals';
import { ClearCartModalId } from '../../organisms/modal/ClearCartModal';
import { addProductClickEvent } from '../../../../utils/customEventHandler';
import { GA4InteractionType } from '../../../../analytics/ga4/types/interaction';
import { selectMyPuratosAccountEmail, selectUserId } from '../../../../selectors/auth';
import { GA4LoginType } from '../../../../analytics/ga4/types/login';
import { GA4PageType } from '../../../../analytics/ga4/types/page';
import { googleEvents } from '../../../../utils/googleEvents';
import { getLanguageIso } from '../../../../selectors/generic';
import { productEventParseSelect } from '../../../../analytics/ga4/parsers/item';
import { isLoggedIn } from '../../../../data/user.model';

class Cart extends Component {
    state = {
        loaded: false,
        cartLoading: false,
    };

    productQuantities = {};

    constructor(props) {
        super(props);

        this.computeQuantities(props);
    }

    componentDidMount() {
        const { mapping } = this.props;
        this.props.fetchCartModel(mapping);
        this.props.fetchCart(mapping);
        this.props.fetchExcelImportModel(mapping);
        this.props.getCartRecommendationModel({ mapping: mapping, pageRoute: PageRoutes.cart });
    }

    componentDidUpdate(oldProps) {
        const { mapping } = this.props;
        /*Received mapping from AEM*/
        if (oldProps.mapping !== mapping) {
            this.props.fetchCartModel(mapping);
            this.props.fetchCart(mapping);
            this.props.getCartRecommendationModel({ mapping: mapping, pageRoute: PageRoutes.cart });
        } else if (
            oldProps.cartData !== this.props.cartData &&
            JSON.stringify(oldProps.cartData) !== JSON.stringify(this.props.cartData)
        ) {
            if (!this.state.loaded) {
                this.setState({
                    loaded: true,
                });
            }
            this.computeQuantities(this.props);
        }

        if (
            oldProps.cartData?.basketItems?.length !== this.props.cartData?.basketItems?.length ||
            oldProps.cartData?.basketBundles?.length !== this.props.cartData?.basketBundles?.length
        ) {
            this.props.getCartRecommendationModel({ mapping: mapping, pageRoute: PageRoutes.cart });
        }
    }

    computeQuantities = props => {
        this.productQuantities = {};

        props.cartData?.basketItems?.forEach(data => {
            const { materialNumber, quantity, isFreeGood } = data;

            if (isFreeGood) {
                return;
            }

            if (!this.productQuantities[materialNumber]) {
                this.productQuantities[materialNumber] = 0;
            }

            this.productQuantities[materialNumber] += quantity;
        });
    };

    setCartLoading = status => {
        this.setState({ cartLoading: status });
    };

    clearCart = mapping => {
        this.props.openModal(ClearCartModalId, { clearCart: this.props.clearCart, mapping });
    };

    onClickProduct = (product, position) => {
        addProductClickEvent(product, GA4InteractionType.cart, false, this.props.userId, this.props.currency, position);
        googleEvents.selectItem({
            user_id: this.props.userId,
            page_language: getLanguageIso(),
            email_address: isLoggedIn() ? this.props.userEmail : '',
            login_status: this.props.userId ? GA4LoginType.LOGGED_IN : GA4LoginType.GUEST,
            website_section: GA4PageType.cart,
            items: productEventParseSelect(product, this.props.currency, GA4InteractionType.cart),
        });
    };

    render() {
        const {
            cartModel,
            checkoutModel,
            i18n,
            loading,
            addItemsToCart,
            cartData,
            onInputChange,
            quickAddItems,
            mapping,
            cartRecommendationsModel,
            cartRecommendationsLoading,
        } = this.props;

        return (
            <CartPage>
                <div className="container cart__content">
                    <div className="row">
                        <AddToCartContextProvider
                            alerts={{
                                addToCart: 'cart.add',
                                addToCartError: 'cart.error',
                                addToCartQuantity: 'cart.quantity',
                            }}
                        >
                            <WithRole
                                roles={[
                                    RoleMatrix.puratosCustomerNoAuth,
                                    RoleMatrix.puratosNoCustomer,
                                    RoleMatrix.puratosCustomerUnvalidated,
                                ]}
                            >
                                <Redirect to={`/${getRoute(mapping, PageRoutes.myPuratos)}`} />
                            </WithRole>

                            <div className="col-md-9 col-sm-12 cart__content--cart">
                                <div className="row">
                                    <div className="col-xs-12">
                                        <Alerts marginTopSm />
                                    </div>
                                </div>

                                <CartList
                                    aemResource={cartModel}
                                    loading={loading}
                                    cartData={cartData}
                                    onInputChange={onInputChange}
                                    quickAddItems={quickAddItems}
                                    addToCart={addItemsToCart}
                                    count={cartData?.basketItems?.length + cartData?.basketBundles?.length || 0}
                                    eCommerceRequired={true}
                                    clearCart={() => this.clearCart(this.props.mapping)}
                                >
                                    <Table>
                                        <CartHeader i18n={i18n} />
                                        <Table.Body>
                                            <GTMContextProvider type={GA4InteractionType.cart}>
                                                {cartData?.basketItems.map((result, index) => (
                                                    <CartRow
                                                        key={result.basketLineId}
                                                        product={result}
                                                        i18n={i18n}
                                                        position={index + 1}
                                                        titlesClickable={true}
                                                        inOverview={false}
                                                        setCartLoading={this.setCartLoading}
                                                        onClickProduct={this.onClickProduct}
                                                    />
                                                ))}
                                                {cartData?.basketBundles.map((result, index) => (
                                                    <BundleCartRow
                                                        key={result.bundleId}
                                                        data={result}
                                                        i18n={i18n}
                                                        position={index + 1}
                                                        titlesClickable={true}
                                                        inOverview={false}
                                                        setCartLoading={this.setCartLoading}
                                                        onClickProduct={this.onClickProduct}
                                                    />
                                                ))}
                                            </GTMContextProvider>
                                        </Table.Body>
                                    </Table>
                                </CartList>

                                <MobileOnly>
                                    <CheckoutCard>
                                        {cartData && <CartInfo i18n={i18n} cartData={cartData} listModel={cartModel} />}
                                    </CheckoutCard>
                                </MobileOnly>

                                <ShowHide
                                    show={cartRecommendationsLoading || cartRecommendationsModel?.products.length > 0}
                                >
                                    <CartRecommendations
                                        resource={cartRecommendationsModel}
                                        loading={cartRecommendationsLoading}
                                    />
                                </ShowHide>
                            </div>

                            <div className="col-md-3 col-sm-12 sticky-top-md">
                                <CheckoutPromo cartData={cartData} view={'desktop'} />

                                {cartData?.basketItems?.length + cartData?.basketBundles?.length > 0 ? (
                                    <CheckoutCard className={'sticky-bottom-sm'}>
                                        {cartData && (
                                            <CartOverview
                                                i18n={i18n}
                                                cartData={cartData}
                                                listModel={cartModel}
                                                showVAT
                                                cartLoading={this.state.cartLoading}
                                                shippingModel={checkoutModel.shippingModel}
                                            />
                                        )}
                                    </CheckoutCard>
                                ) : null}
                                <DesktopOnly>
                                    <CheckoutCard>
                                        {cartData && <CartInfo i18n={i18n} cartData={cartData} listModel={cartModel} />}
                                    </CheckoutCard>
                                </DesktopOnly>
                            </div>
                        </AddToCartContextProvider>
                    </div>
                </div>
            </CartPage>
        );
    }
}

const mapStateToProps = state => ({
    cartModel: state.myPuratos_cart.aemResource,
    checkoutModel: state.myPuratos_checkout.aemResource,
    cartData: state.myPuratos_cart.data,
    interestingProductsModel: state.myPuratos_interestingProducts.aemResource,
    interestingProducts: state.myPuratos_interestingProducts.interProducts,
    i18n: state.myPuratos_i18n.i18n,
    loading: isCartLoading(state) || isLoadingById(state, FETCH_CART) || !isCartLoaded(state),
    interestingLoading: state.myPuratos_interestingProducts.loading,
    mapping: state.myPuratos_dashboard.mapping,
    quickAddItems: state.myPuratos_cart.quickAddItems,
    homepage: state.myPuratos_aem.homepage,
    cartInputDisabled: state.myPuratos_cart.disabled,
    cartRecommendationsModel: state.myPuratos_cartRecommendations.aemResource,
    cartRecommendationsLoading: state.myPuratos_cartRecommendations.loading,
    userId: selectUserId(state),
    currency: getCurrencyCode(state),
    userEmail: selectMyPuratosAccountEmail(state),
});

const mapDispatchToProps = {
    fetchCartModel: fetchCartModel,
    fetchCart: fetchCart,
    fetchInterestingProducts: fetchInterestingProducts,
    getCartRecommendationModel: getCartRecommendationsModel,
    updateItem: updateItem,
    deleteItem: deleteItem,
    onInputChange: fetchQuickAddItems,
    clearCart: clearCart,
    addItemsToCart: addItemsToCart,
    fetchExcelImportModel,
    setCheckoutStep,
    openModal,
};

export default connect(mapStateToProps, mapDispatchToProps)(Cart);
